import { useState } from "react";
import StatusLights from "./StatusLights";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedAppointment } from "../../store/slices/appointmentsSlice";

function FutureAppointments({ appointments = [], onAppointmentClick }) {
  const [filter, setFilter] = useState("All"); // 'All', 'Future', 'Previous'
  const dispatch = useDispatch();

  const calculateDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const duration = (endDate - startDate) / (1000 * 60); // Duration in minutes
    return duration;
  };

  function formatPrice(price) {
    if (price === undefined || price === null) {
      return "$0.00"; // Return a default value or handle it as needed
    }
    const numPrice = Number(price);
    return `$${numPrice.toFixed(2)}`;
  }

  const filteredAppointments = appointments.filter((appointment) => {
    const appointmentDate = new Date(appointment.start);
    const now = new Date();

    if (filter === "Future") {
      return appointmentDate > now;
    } else if (filter === "Previous") {
      return appointmentDate < now;
    }
    return true; // 'All' appointments
  });

  const uniqueAppointments = Array.from(
    new Set(filteredAppointments.map((a) => a._id))
  ).map((id) => filteredAppointments.find((a) => a._id === id));

  return (
    <div className="space-y-4 pt-3">
      <div className="flex flex-col space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-900">Appointments</h2>

          {/* Filter Selector */}
          <div className="flex items-center gap-x-2">
            <label className="text-xs font-medium text-gray-500">Filter:</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="inline-flex h-7 items-center justify-between rounded-sm border border-gray-200 bg-white px-2 py-1 text-xs ring-offset-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-950 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50">
              <option value="All">All</option>
              <option value="Future">Future</option>
              <option value="Previous">Previous</option>
            </select>
          </div>
        </div>

        <div className="rounded-sm border border-gray-100/50 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="bg-gray-50/30 px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 min-w-[100px]">
                    Clinic
                  </th>
                  <th className="bg-gray-50/30 px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 min-w-[120px]">
                    Appointment
                  </th>
                  <th className="bg-gray-50/30 px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 min-w-[140px]">
                    Date & Time
                  </th>
                  <th className="bg-gray-50/30 px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 min-w-[60px] hidden sm:table-cell">
                    Block #
                  </th>
                  <th className="bg-gray-50/30 px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 min-w-[100px]">
                    Client
                  </th>
                  <th className="bg-gray-50/30 px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 min-w-[100px] hidden lg:table-cell">
                    Treating Clinician
                  </th>
                  <th className="bg-gray-50/30 px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 min-w-[100px] hidden lg:table-cell">
                    Supervising Clinician
                  </th>
                  <th className="bg-gray-50/30 px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 min-w-[70px] hidden md:table-cell">
                    Amount
                  </th>
                  <th className="bg-gray-50/30 px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 min-w-[80px]">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {uniqueAppointments.length > 0 ? (
                  uniqueAppointments.map((appointment, index) => (
                    <tr
                      key={`${appointment._id}-${index}`}
                      className="group hover:bg-gray-50/50 cursor-pointer transition-all"
                      onClick={() => {
                        console.log(
                          "[Future Appointments] Row clicked, onAppointmentClick prop:",
                          !!onAppointmentClick
                        );
                        // Always update Redux selected appointment for AppointmentSummary to display it
                        dispatch(setSelectedAppointment(appointment));

                        if (onAppointmentClick) {
                          // Also call the prop callback if provided
                          onAppointmentClick(appointment);
                        }
                      }}>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                        <div className="truncate">
                          <span className="text-xs text-gray-900">
                            {appointment.clinic?.name || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-2">
                        <div className="truncate">
                          <p className="text-xs text-gray-900 truncate group-hover:text-gray-950">
                            {appointment.title || "N/A"}
                          </p>
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-2">
                        <time
                          dateTime={appointment.datetime}
                          className="text-xs text-gray-500">
                          <div className="sm:hidden">
                            {new Date(appointment.start).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                              }
                            )}
                            <br />
                            {new Date(appointment.start).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              }
                            )}
                          </div>
                          <div className="hidden sm:block">
                            {new Date(appointment.start).toLocaleDateString(
                              "en-US",
                              {
                                weekday: "short",
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )}
                            ,{" "}
                            {new Date(appointment.start).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              }
                            )}
                          </div>
                        </time>
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap hidden sm:table-cell">
                        <span className="text-xs text-gray-900">
                          {appointment.blockNumber
                            ? appointment.blockNumber
                            : "N/A"}
                        </span>
                      </td>
                      <td className="px-2 sm:px-3 py-2">
                        <div className="truncate">
                          <span className="text-xs text-gray-900">
                            <div className="sm:hidden">
                              {appointment.client?.firstName?.[0]}.{" "}
                              {appointment.client?.lastName}
                            </div>
                            <div className="hidden sm:block">
                              {appointment.client?.firstName}{" "}
                              {appointment.client?.lastName}
                            </div>
                          </span>
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap hidden lg:table-cell">
                        <div className="truncate">
                          <span className="text-xs text-gray-900">
                            {appointment.clinician?.firstName &&
                            appointment.clinician?.lastName
                              ? `${appointment.clinician.firstName} ${appointment.clinician.lastName}`
                              : "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap hidden lg:table-cell">
                        <div className="truncate">
                          <span className="text-xs text-gray-900">
                            {appointment.supervisingClinician?.firstName &&
                            appointment.supervisingClinician?.lastName
                              ? `${appointment.supervisingClinician.firstName} ${appointment.supervisingClinician.lastName}`
                              : "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap hidden md:table-cell">
                        <span className="text-xs text-gray-900">
                          {appointment.service
                            ? formatPrice(appointment.service.price)
                            : "$0.00"}
                        </span>
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                        <StatusLights appointment={appointment} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-2 sm:px-3 py-6 text-center text-xs text-gray-500">
                      No {filter.toLowerCase()} appointments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal removed - now handled in parent components */}
    </div>
  );
}

export default FutureAppointments;
